import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Briefcase, Send, Star, Users, Loader2, Clock, Eye } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function EmployerDashboard() {
  const { t } = useLang();
  const [d, setD] = useState(null);
  const [jobs, setJobs] = useState([]);

  const load = () => {
    api.get("/employer/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/jobs/mine").then((r) => setJobs(r.data.results)).catch(() => {});
  };
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/jobs/${id}/status`, null, { params: { status } }); toast.success(`Job ${status}`); load(); }
    catch { toast.error("Failed"); }
  };

  if (!d) return <Layout><div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-brand-blue" /></div></Layout>;

  const CUR = { INR: "₹", USD: "$" };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-navy">{d.company_name || "Employer"}</h1>
            <p className="mt-1 text-sm text-slate-500">Welcome back to your hiring command centre.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/find-people" className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-navy hover:bg-brand-bluelight">Find People</Link>
            <Link to="/employer/post-job" data-testid="post-job-btn" className="gjt-btn-orange inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold"><Plus className="h-4 w-4" /> {t("nav_post_job")}</Link>
          </div>
        </div>

        {/* Free credits banner */}
        <div data-testid="free-credits-banner" className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="gjt-card p-6 border-l-4 border-l-emerald-500">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("free_posts_remaining")}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-navy"><span data-testid="free-credits-count">{d.free_credits}</span> / 5</p>
          </div>
          <div className="gjt-card p-6 border-l-4 border-l-brand-blue">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{t("free_period_left")}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-navy">{d.free_period_days_left} <span className="text-base font-medium text-slate-500">{t("days")}</span></p>
          </div>
        </div>

        <div className="mt-6 grid gap-4 grid-cols-2 lg:grid-cols-5">
          {[
            { icon: Briefcase, label: t("active_jobs"), v: d.active_jobs },
            { icon: Clock, label: "Expired", v: d.expired_jobs },
            { icon: Send, label: t("applications"), v: d.applications },
            { icon: Star, label: t("shortlisted"), v: d.shortlisted },
            { icon: Users, label: t("invited"), v: d.invited },
          ].map((s, i) => (
            <div key={i} className="gjt-card p-5">
              <s.icon className="h-5 w-5 text-brand-blue" />
              <p className="mt-2 font-display text-2xl font-extrabold text-navy">{s.v}</p>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-10 font-display text-xl font-bold text-navy">Your jobs</h2>
        {jobs.length === 0 ? (
          <p className="mt-6 text-center text-sm text-slate-400">No jobs yet. <Link to="/employer/post-job" className="font-semibold text-brand-blue">Post your first free job →</Link></p>
        ) : (
          <div className="mt-4 space-y-3">
            {jobs.map((j) => (
              <div key={j.id} data-testid={`emp-job-${j.id}`} className="gjt-card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${j.status === "active" ? "bg-emerald-500" : "bg-slate-300"}`} />
                    <Link to={`/jobs/${j.id}`} className="font-semibold text-navy hover:text-brand-blue">{j.title}</Link>
                    {j.is_free && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-600">Free</span>}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{j.city}, {j.country} · {j.applications || 0} applications · <Eye className="inline h-3 w-3" /> {j.views || 0} views · expires {new Date(j.expiry_date).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  {!j.is_free && <span className="text-sm font-semibold text-navy">{(CUR[j.payment_currency] || "$")}{j.amount_paid}</span>}
                  {j.status === "active"
                    ? <button data-testid={`close-job-${j.id}`} onClick={() => setStatus(j.id, "expired")} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Close</button>
                    : <button data-testid={`reopen-job-${j.id}`} onClick={() => setStatus(j.id, "active")} className="rounded-xl border border-slate-200 px-3 py-1.5 text-sm font-semibold text-brand-blue hover:bg-brand-bluelight">Reopen</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
